import Component from '@glimmer/component';
import { action, setProperties, get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { addSeconds, getUnixTime, startOfDay } from 'date-fns';
import { SECONDS_IN_A_DAY } from 'garaje/utils/enums';
import { buildInitialsMarkerHtml, buildPhotoMarkerHtml } from 'garaje/utils/map/map-marker-html';
import { DESK_ICON, RED_PIN_ICON } from 'garaje/utils/map/map-icons';

/**
 * MoveRequestMap Component
 *
 * Displays a floor map for selecting a desk for a move request.
 * Shows all desks with appropriate icons based on availability:
 * - Available (green): no assignment, not shared → clickable
 * - Shared/scheduled (gray + clock): on a schedule → not clickable
 * - Unavailable (gray): assigned or disabled → not clickable
 * - Selected (red pin): the user's chosen desk
 *
 * @param {Array<MapFloor>} floors          Available floors
 * @param {string|null}     currentFloorId  Pre-selected floor ID
 * @param {Desk|null}       selectedDesk    Currently selected desk
 * @param {Function}        onDeskSelect    Callback: (desk|null) => void
 */
export default class MoveRequestMapComponent extends Component {
  @service state;
  @service store;

  @tracked isMapReady = false;
  @tracked mapHeight;
  @tracked mapWidth;
  @tracked currentFloor;
  @tracked desks = [];

  get floorsWithMap() {
    return (this.args.floors ?? []).filter((floor) => floor.rasterImageUrl);
  }

  /**
   * Get desks on the current floor that are placed on the map.
   */
  get placedDesks() {
    if (!this.currentFloor) {
      return [];
    }
    return this.desks.filter((desk) => get(desk, 'placed') && !get(desk, 'isDeleted'));
  }

  /**
   * Resolve the tooltip state for a desk on the move date.
   * Precedence: Disabled → Restricted → Assigned → Available.
   *
   * Restricted: the desk's neighborhood has a non-empty employeeIds
   * allow-list that does not include the requesting employee.
   * Assigned: someone other than the requesting employee holds the desk.
   */
  @action
  deskTooltipState(desk) {
    if (!get(desk, 'enabled')) return 'disabled';

    const neighborhoodId = desk.belongsTo('neighborhood').id();
    if (neighborhoodId) {
      const neighborhood = this.store.peekRecord('neighborhood', neighborhoodId);
      const allowList = neighborhood?.employeeIds ?? [];
      if (allowList.length > 0 && !allowList.includes(this.args.employeeId)) {
        return 'restricted';
      }
    }

    const assignedTo = get(desk, 'assignedTo');
    if (assignedTo && assignedTo !== this.args.employeeEmail) {
      return 'assigned';
    }

    return 'available';
  }

  /**
   * Apply icons to desks based on their status and selection state.
   */
  get placedDesksWithIcons() {
    const selectedDeskId = this.args.selectedDesk ? get(this.args.selectedDesk, 'id') : null;

    return this.placedDesks.map((desk) => {
      const deskId = get(desk, 'id');
      const isSelected = deskId === selectedDeskId;

      if (isSelected) {
        this.setDeskIcon(desk, RED_PIN_ICON);
      } else if (get(desk, 'assignedTo')) {
        this.setAssigneeIcon(desk, get(desk, 'assignedTo'));
      } else {
        this.setDeskIcon(desk, DESK_ICON);
      }

      return desk;
    });
  }

  setDeskIcon(desk, icon) {
    setProperties(desk, {
      iconClassname: icon.iconClassname,
      iconSize: icon.iconSize,
      iconHtml: '',
    });
  }

  setAssigneeIcon(desk, email) {
    if (this.isDestroyed || this.isDestroying) return;
    const employee = this.store.peekAll('employee').find((e) => get(e, 'email') === email);
    const profilePictureUrl = employee?.profilePictureUrl;

    const iconHtml =
      profilePictureUrl && !profilePictureUrl.includes('gravatar.com')
        ? buildPhotoMarkerHtml(profilePictureUrl)
        : buildInitialsMarkerHtml(employee?.name ?? email);

    setProperties(desk, { iconClassname: '', iconSize: [30, 30], iconHtml });
  }

  @dropTask
  *loadFloorsTask() {
    // yield to satisfy generator requirement
    yield Promise.resolve();
    if (this.floorsWithMap.length > 0) {
      // Pre-select floor matching the current live map floor, or the first available
      const matchingFloor = this.args.currentFloorId
        ? this.floorsWithMap.find((f) => f.id === this.args.currentFloorId)
        : null;
      this.currentFloor = matchingFloor ?? this.floorsWithMap[0] ?? null;
    } else {
      this.currentFloor = null;
    }
  }

  @restartableTask
  *loadDesksTask() {
    if (!this.currentFloor) {
      return;
    }

    // Request date-scoped `assigned-to` decoration per
    // envoy-spaces/docs/desks-api-date-scoped-assignee.md: passing
    // `fields[desks]=accessible` together with the availability range
    // triggers CoreDeskService's decoration block, which overwrites
    // `assigned-to`, `neighborhood-id`, and `neighborhood-name` with
    // the assignment effective on the move date (falling back to the
    // permanent owner). `fields[desks]=accessible` alone does not run
    // `filterAccessibleDesks`, so desks the session user lacks
    // neighborhood access to are still returned — which is what we
    // want on the move-request map. `userIdFilter` falls back to the
    // session user, so no email param is required.
    const params = {
      filter: {
        'location-id': get(this.state.currentLocation, 'id'),
      },
      fields: { desks: 'accessible' },
      include: 'amenities',
    };

    if (this.args.moveDate) {
      const dayStart = startOfDay(this.args.moveDate);
      params.filter['available-from'] = getUnixTime(dayStart);
      params.filter['available-to'] = getUnixTime(addSeconds(dayStart, SECONDS_IN_A_DAY - 1));
    }

    const desks = yield this.store.query('desk', params);
    this.desks = desks.filter((desk) => get(desk, 'floor.id') === this.currentFloor?.id);
  }

  @dropTask
  *onModalLoad() {
    yield this.loadFloorsTask.perform();
    yield this.loadDesksTask.perform();
    yield this.loadMapImageTask.perform();
  }

  @dropTask
  *loadMapImageTask() {
    if (!this.currentFloor) {
      return;
    }

    yield new Promise((resolve, reject) => {
      const image = new Image();
      image.src = this.currentFloor.rasterImageUrl;
      image.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error('error loading map', err);
        reject();
      };
      image.onload = (ev) => {
        if (!this.isDestroyed && !this.isDestroying) {
          const { height: mapHeight, width: mapWidth } = ev.target;
          setProperties(this, {
            mapHeight,
            mapWidth,
          });
        }
        resolve();
      };
    });
  }

  @dropTask
  *onFloorChange(floor) {
    this.currentFloor = floor;
    // Notify parent so it can persist the floor selection across phase changes
    if (this.args.onFloorChange) {
      this.args.onFloorChange(floor.id);
    }
    // Clear selected desk when switching floors
    if (this.args.onDeskSelect) {
      this.args.onDeskSelect(null);
    }
    yield this.loadDesksTask.perform();
    yield this.loadMapImageTask.perform();
  }

  @action
  reloadDesks() {
    this.loadDesksTask.perform();
  }

  @action
  toggleIsMapReady() {
    this.isMapReady = true;
  }

  @action
  handleDeskClick(desk) {
    const selectedDeskId = this.args.selectedDesk ? get(this.args.selectedDesk, 'id') : null;
    const clickedDeskId = get(desk, 'id');

    if (clickedDeskId === selectedDeskId) {
      // Deselect
      this.args.onDeskSelect(null);
    } else {
      // Select
      this.args.onDeskSelect(desk);
    }
  }
}
